import styles from './LegalPage.module.css';

export function Privacy() {
  return (
    <div className={styles.legalPage}>
      <h1 className={styles.title}>Privacy Policy</h1>
      <p className={styles.lastUpdated}>Last updated: January 2026</p>

      <div className={styles.content}>
        <h2>1. Introduction</h2>
        <p>
          TOKO Math Club ("we", "our", or "us") operates the TOKO Add, TOKO Multiply,
          and related educational applications. This Privacy Policy explains what information
          we collect, how we use it, and your rights regarding your data.
        </p>
        <p>
          Our apps are designed for children learning math with parental supervision.
          We are committed to protecting the privacy of all users, especially children.
        </p>

        <h2>2. Information We Collect</h2>

        <h3>Account Information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li><strong>Email address</strong> — Used for account authentication and login</li>
          <li><strong>Username</strong> — A display name you choose, visible during multiplayer races</li>
        </ul>

        <h3>Learning Progress Data</h3>
        <p>When you complete practice sessions, we store:</p>
        <ul>
          <li>Which problems were practiced and when</li>
          <li>Time taken per problem and overall session time</li>
          <li>Accuracy (correct, incorrect, and missed answers)</li>
          <li>Current unit progress in each learning schedule</li>
        </ul>
        <p>
          This data allows you to track your progress and see which facts need more practice.
        </p>

        <h3>Race Participation Data</h3>
        <p>When you join multiplayer races, we collect:</p>
        <ul>
          <li>Your username and which race room you joined</li>
          <li>Your performance during the race (answers and timing)</li>
          <li>Your position on the leaderboard</li>
        </ul>
        <p>
          Race hosts (teachers or parents) can see the performance of all participants
          in races they create.
        </p>

        <h3>Device Storage</h3>
        <p>
          The apps store settings and cached progress locally on your device. This data
          remains on your device and is not transmitted to our servers unless you sign in
          to sync your progress.
        </p>

        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide the learning experience and track your progress</li>
          <li>Display performance analytics so you can see which facts need practice</li>
          <li>Enable multiplayer race features</li>
        </ul>

        <h2>4. Information We Do Not Collect</h2>
        <p>We believe in minimal data collection. We do not collect:</p>
        <ul>
          <li><strong>Advertising data</strong> — Our apps have no ads and no ad tracking</li>
          <li><strong>Third-party analytics</strong> — We do not use Google Analytics, Mixpanel, or similar services</li>
          <li><strong>Location data</strong> — We do not track your location</li>
          <li><strong>Device identifiers</strong> — We do not fingerprint your device</li>
          <li><strong>Browsing behavior</strong> — We do not track activity outside our apps</li>
        </ul>
        <p>
          We do not sell your personal information. We do not share your data with
          advertisers or data brokers.
        </p>

        <h2>5. Data Storage and Security</h2>
        <p>
          Your data is stored securely using Supabase, a trusted cloud infrastructure provider.
          We implement appropriate security measures including:
        </p>
        <ul>
          <li>Encryption of data in transit (HTTPS/TLS)</li>
          <li>Secure password hashing</li>
          <li>Row-level security ensuring users can only access their own data</li>
          <li>Secure authentication protocols</li>
        </ul>

        <h2>6. Children's Privacy</h2>
        <p>
          Our apps are designed for use by children with parental or teacher supervision.
          We take children's privacy seriously:
        </p>
        <ul>
          <li>We collect only the minimum data necessary for the learning experience</li>
          <li>We do not require children to provide personal information beyond a username</li>
          <li>We do not serve advertisements to children</li>
          <li>We do not use data for behavioral targeting</li>
          <li>Parents can contact us to request deletion of their child's data</li>
        </ul>
        <p>
          We comply with the Children's Online Privacy Protection Act (COPPA) and
          applicable children's privacy regulations.
        </p>

        <h2>7. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> — View the data we have collected about you</li>
          <li><strong>Correction</strong> — Update inaccurate information</li>
          <li><strong>Deletion</strong> — Request deletion of your account and associated data</li>
          <li><strong>Portability</strong> — Request a copy of your data</li>
        </ul>
        <p>
          If you are in the European Economic Area, you also have rights under the General
          Data Protection Regulation (GDPR) including the right to object to processing
          and to withdraw consent.
        </p>
        <p>
          To exercise any of these rights, please contact us using the information below.
        </p>

        <h2>8. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify users of
          significant changes by updating the "Last updated" date at the top of this page.
        </p>

        <h2>9. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or wish to exercise your rights,
          please contact us at:
        </p>
        <p>
          <a href="mailto:office@AIquadrat.com">office@AIquadrat.com</a>
        </p>
      </div>
    </div>
  );
}
